import { Check } from "lucide-react";
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/ui/dropdown-menu";
import { useAuthV2, type PersonaOption, type PersonaState } from "@/lib/authv2/store";
import { activePersona, hasPersonas, personaOptions } from "@/lib/authv2/persona";
import { PersonaIconBadge } from "./PersonaIcon";
import { usePersonaSwitch } from "./usePersonaSwitch";

/**
 * Dòng gợi ý dưới tên danh phận (bản mẫu v2): danh phận chính luôn ghi "Danh
 * phận chính" — dấu ✓ đã nói nó đang dùng; danh phận phụ đang dùng ghi "Đang dùng".
 */
function personaHint(p: PersonaOption, isPrimary: boolean, isActive: boolean): string {
  if (isPrimary) return "Danh phận chính";
  if (isActive) return "Đang dùng";
  const role = p.role.toLowerCase();
  const dept = p.department.toLowerCase();
  if (role === "qc") return "Chấm bài · ghi lỗi";
  if (dept.startsWith("photo")) return "Đăng ký ca · đi chụp · nộp ảnh";
  if (dept === "makeup") return "Đăng ký ca · đi trang điểm";
  if (dept === "retouch") return "Nhận đơn sửa · nộp ảnh";
  return "Danh phận phụ";
}

type Variant = "dropdown" | "sheet";

/**
 * Khối "Dùng với tư cách" (QĐ 0255). Không có danh phận phụ thì không vẽ gì —
 * và cũng không đụng tới react-query, để màn chưa bọc QueryClient vẫn chạy.
 */
export function PersonaMenuSection({ variant }: { variant: Variant }) {
  const state = useAuthV2((s) => s.persona);
  if (!hasPersonas(state)) return null;
  return <PersonaMenuSectionInner variant={variant} state={state} />;
}

function PersonaMenuSectionInner({ variant, state }: { variant: Variant; state: PersonaState }) {
  const sw = usePersonaSwitch();
  const current = activePersona(state);
  const pick = (p: PersonaOption) => {
    if (p.id === current.id || sw.isPending) return;
    sw.mutate(p);
  };
  const rows = personaOptions(state).map((p) => {
    const isActive = p.id === current.id;
    return {
      p,
      isActive,
      hint: personaHint(p, p.id === state.primary.id, isActive),
    };
  });

  if (variant === "sheet") {
    return (
      <div className="space-y-1" data-testid="persona-menu">
        <p className="px-1 text-xs font-semibold uppercase text-muted-foreground">
          Dùng với tư cách
        </p>
        {rows.map(({ p, isActive, hint }) => (
          <button
            key={p.id || "primary"}
            type="button"
            data-testid="persona-item"
            aria-current={isActive ? "true" : undefined}
            disabled={sw.isPending}
            onClick={() => pick(p)}
            className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-muted/50 disabled:opacity-60"
          >
            <PersonaIconBadge role={p.role} department={p.department} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{p.label}</span>
              <span className="block truncate text-xs text-muted-foreground">{hint}</span>
            </span>
            {isActive && <Check className="h-4 w-4 shrink-0 text-info-text" aria-hidden />}
          </button>
        ))}
      </div>
    );
  }

  return (
    <>
      <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
        Dùng với tư cách
      </DropdownMenuLabel>
      {rows.map(({ p, isActive, hint }) => (
        <DropdownMenuItem
          key={p.id || "primary"}
          data-testid="persona-item"
          aria-current={isActive ? "true" : undefined}
          disabled={sw.isPending}
          onSelect={(e) => {
            if (isActive) e.preventDefault();
            pick(p);
          }}
          className="gap-3"
        >
          <PersonaIconBadge role={p.role} department={p.department} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">{p.label}</span>
            <span className="block truncate text-xs text-muted-foreground">{hint}</span>
          </span>
          {isActive && <Check className="h-4 w-4 shrink-0 text-info-text" aria-hidden />}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
    </>
  );
}
